/**
 * AUT-440 (WEB-003). The fifth stat tile in the keyword-, theme- and product-detail views: the summed allocated
 * Sponsored-Products spend over the modal's own window (FR-16). A leaf under the modal.
 *
 * The card follows **gate 1** (`components/rank-tracker.md § The two gates on the wire`): its parent
 * renders it only when the resolved row's `adSpend` is non-null, so this component never has to model the
 * no-ad-rows state — that collapses the tile grid to four in `index.tsx`.
 *
 * Two divergences from the design capture resolve toward the ticket here
 * (`components/rank-tracker.md § The screen`):
 * - **the `$0` state.** The prototype had none — an all-`$0` window drew a flat zero area with the
 *   ordinary caption. The ticket adds a dedicated copy, `No Sponsored Products spend`, shown whenever
 *   no day in the window carried positive spend (FR-16).
 * - **the shared-spend label.** Absent from the prototype (no ad-group concept). When the keyword's
 *   ad group spans more than one ASIN over the range, the spend is an estimate and the card says so
 *   (FR-19). `sharedSpendLabel` is the single source of that string, read by both this card and the
 *   chart tooltip (`index.tsx`).
 *
 * The card is **window-scoped**: `total` and `hasWindowSpend` are computed by the parent over the
 * days actually drawn, not from the response-level `adSpendTotal`/`hasAdSpend` (which span the API's
 * whole window) — the fourth divergence the ticket resolves.
 */

/** The spend series/legend name at keyword level. */
export const AD_SPEND_SERIES_NAME = "Daily Ad Spend";

/** The card's label at theme and product level -- the roll-up over the tracked keywords beneath the
 * row, each match key once. One place for the strings so the card and the legend cannot drift. */
export const ROLLUP_AD_SPEND_LABEL = "Ad spend on tracked keywords";

/** The spend series/legend name at theme and product level. */
export const ROLLUP_AD_SPEND_SERIES_NAME = "Ad Spend on Tracked Keywords";

/** The card's copy when the window carried no positive spend (FR-16). */
export const NO_AD_SPEND_COPY = "No Sponsored Products spend";

/** The single source of the shared-spend disclosure, read by the card and the chart tooltip so the
 * two never drift (FR-19). `n` is `maxAsinsInAdGroup`. */
export function sharedSpendLabel(n: number): string {
  return `Estimated · shared across up to ${n} products`;
}

/** Whole-dollar USD with thousands separators — the tile's amount (`$0`, `$72`, `$1,284`). The
 * tooltip formats the same figure to 2 decimals; the two are deliberately different precisions
 * (`design-input/rank-tracker-ad-spend/sources.md § 7.3`). */
export function formatUsdWhole(value: number): string {
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export interface AdSpendCardProps {
  /** The card's label: `Ad spend` at keyword level, `ROLLUP_AD_SPEND_LABEL` at theme/product level. */
  label: string;
  /** Window-scoped sum of allocated spend over the visible days. */
  total: number;
  /** True when at least one visible day carried positive spend — the window-scoped equivalent of
   * `hasAdSpend`. False here (an all-`$0` or entirely-missing window) shows `No Sponsored Products
   * spend` rather than `$0`. */
  hasWindowSpend: boolean;
  /** `N` in `Sponsored Products · last N days` — the number of days actually drawn. */
  dayCount: number;
  /** `maxAsinsInAdGroup` for this keyword, theme or product over the range. `> 1` makes the spend a shared estimate
   * (FR-19); `1` (or fewer) shows no disclosure. */
  maxAsinsInAdGroup: number;
}

/** The mockup's `StatTile` shape (label / value / caption), with a $0 state and an optional
 * shared-spend line the generic tile does not carry. Kept as its own component rather than the
 * shared `StatTile` because those two extra states are specific to spend. */
export function AdSpendCard({ label, total, hasWindowSpend, dayCount, maxAsinsInAdGroup }: AdSpendCardProps) {
  const isShared = maxAsinsInAdGroup > 1;
  return (
    <div className="rounded-sm bg-muted/50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      {hasWindowSpend ? (
        <>
          <p className="mt-1 text-2xl font-semibold text-foreground-strong tabular-nums">
            {formatUsdWhole(total)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Sponsored Products · last {dayCount} days
          </p>
          {isShared && (
            <p className="mt-1 text-xs text-muted-foreground">{sharedSpendLabel(maxAsinsInAdGroup)}</p>
          )}
        </>
      ) : (
        <p className="mt-1 text-sm font-medium text-muted-foreground">{NO_AD_SPEND_COPY}</p>
      )}
    </div>
  );
}
